import { Colors, PageContainer, Text } from '@amelio/ui-library';
import { useParams } from 'react-router-dom';

import { CareerAspirationSection } from './components/CareerAspirationSection';
import { CompletionSection } from './components/CompletionSection';
import { MeetingNotesSection } from './components/MeetingNotesSection';
import { MeetingPlanHeader } from './components/MeetingPlanHeader';
import { MeetingTipsSection } from './components/MeetingTipsSection';
import { PendingSavesScope } from './components/PendingSavesScope';
import { PreparationSection } from './components/PreparationSection';
import { ThematicMeetingAgenda } from './components/ThematicMeetingAgenda';
import { ThematicMeetingTranscriptSection } from './components/ThematicMeetingTranscriptSection';

import { isRefusedError } from '@/api/api';
import { useGetThematicMeeting, useGetThematicMeetingPlan } from '@/api/thematic-meetings';
import { PlanCard } from '@/components/MeetingPlan/PlanSection';
import { NotFoundState } from '@/components/NotFoundState/NotFoundState';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { useAmelioTranslations } from '@/hooks';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import { useNavigate } from '@/hooks/utils/useNavigate';
import { RouteName } from '@/navigation/types';
import {
    CARD_STACK_GAP,
    CAREER_SECTION_OFFSET,
    NOTES_SECTION_OFFSET,
    PLAN_CONTENT_MAX_WIDTH,
    TRANSCRIPT_SECTION_OFFSET,
} from '@/pages/ThematicMeetings/components/meetingStyles';

// `page-frame-inset` gives the frame the module's phone inset and its own 28px above `md` (see
// src/responsive-utilities.css). `thematic-meetings-plan-layout` lifts the ambient page cap of that
// same file, so the row's own `PLAN_CONTENT_MAX_WIDTH` is the one that binds.
const PLAN_CONTENT_CLASSNAME = `thematic-meetings-plan-layout page-frame-inset flex flex-col ${CARD_STACK_GAP} ${PLAN_CONTENT_MAX_WIDTH}`;

/**
 * A failure worth telling the user to retry. The server answers 404 for a meeting outside the
 * manager's reach as for one that does not exist, and 403 for a reader who may not manage
 * meetings: neither changes on a retry, so neither is a fault.
 */
const isFault = (error: unknown): boolean => !!error && !isRefusedError(error);

const ThematicMeetingPlan = () => {
    const { t } = useAmelioTranslations();
    const navigate = useNavigate();
    const { meetingId } = useParams<{ meetingId: string }>();

    const { data: meeting, isLoading, error: meetingError } = useGetThematicMeeting(meetingId);
    const {
        data: template,
        isLoading: isTemplateLoading,
        error: templateError,
    } = useGetThematicMeetingPlan(meetingId);

    // Only faults reach the generic toast: a refusal already has its own screen below.
    useFetchErrorsHandler({ errors: [isFault(meetingError), isFault(templateError)] });

    const handleBack = () => navigate({ path: RouteName.ThematicMeetings });

    if (isRefusedError(meetingError) || isRefusedError(templateError)) {
        return (
            <NotFoundState
                titleKey={'errors.notFoundOrInaccessibleTitle'}
                messageKey={'errors.notFoundOrInaccessibleMessage'}
                backTo={{
                    labelKey: 'thematicMeetings.plan.backToMeetings',
                    path: RouteName.ThematicMeetings,
                }}
            />
        );
    }

    if (meetingError || templateError) {
        return (
            <PageContainer>
                <PlanCard dataTestId={'thematic-meeting-plan-error'}>
                    <Text color={Colors.ERROR_800}>
                        {t('thematicMeetings.plan.errorLoadingMeeting')}
                    </Text>
                </PlanCard>
            </PageContainer>
        );
    }

    if (isLoading || isTemplateLoading || !meeting || !template) {
        return (
            <PageContainer>
                <div
                    className={PLAN_CONTENT_CLASSNAME}
                    data-testid={'thematic-meeting-plan-skeleton'}
                >
                    <Skeleton>
                        <div className={'space-y-6'}>
                            <Bone className={'h-7.5 w-50'} />
                            <Bone className={'h-40 w-full'} />
                            <Bone className={'h-30 w-full'} />
                            <Bone className={'h-60 w-full'} />
                        </div>
                    </Skeleton>
                </div>
            </PageContainer>
        );
    }

    const isCompleted = meeting.status === 'Completed';

    return (
        <PageContainer>
            <div
                className={PLAN_CONTENT_CLASSNAME}
                data-testid={'thematic-meeting-plan-content'}
            >
                <MeetingPlanHeader
                    meeting={meeting}
                    template={template}
                    onBack={handleBack}
                />

                <MeetingTipsSection template={template} />

                <PreparationSection
                    meeting={meeting}
                    template={template}
                    disabled={isCompleted}
                />

                <ThematicMeetingAgenda
                    meetingId={meeting.id}
                    agenda={meeting.agenda}
                    context={meeting.context}
                    disabled={isCompleted}
                />

                {/* Career meetings only, and after the agenda: the page reads as a plan first. */}
                {meeting.topic === 'Career' && (
                    <div className={CAREER_SECTION_OFFSET}>
                        <CareerAspirationSection
                            meeting={meeting}
                            disabled={isCompleted}
                        />
                    </div>
                )}

                {/* Offered in every status and never read-only: the manager often holds the file
                    before marking the meeting completed, and a transcript added afterwards changes
                    nothing the completion froze. */}
                <div className={TRANSCRIPT_SECTION_OFFSET}>
                    <ThematicMeetingTranscriptSection meetingId={meeting.id} />
                </div>

                {/* Last of the plan, after what the meeting produced: notes are written once it is
                    held, not while it is being prepared. */}
                <div className={NOTES_SECTION_OFFSET}>
                    <MeetingNotesSection
                        meeting={meeting}
                        disabled={isCompleted}
                    />
                </div>

                <CompletionSection
                    meetingId={meeting.id}
                    status={meeting.status}
                />
            </div>
        </PageContainer>
    );
};

/** The plan's editors save on request: completing the meeting saves what they still hold first. */
export const ThematicMeetingPlanPage = () => (
    <PendingSavesScope>
        <ThematicMeetingPlan />
    </PendingSavesScope>
);
